import { useMemo, useState } from 'react';
import Seo from '@/components/feature/Seo';
import Breadcrumb from '@/components/base/Breadcrumb';
import WorkCard from '@/components/feature/WorkCard';
import { works } from '@/mocks/works';

const types = ['全部', '电影', '电视剧', '纪录片'] as const;
type TypeFilter = (typeof types)[number];

const STEP = 6;

export default function WorksList() {
  const [type, setType] = useState<TypeFilter>('全部');
  const [year, setYear] = useState('全部');
  const [sort, setSort] = useState<'latest' | 'popular'>('latest');
  const [visible, setVisible] = useState(STEP);
  const [filterOpen, setFilterOpen] = useState(false);

  const years = useMemo(
    () => ['全部', ...Array.from(new Set(works.map((w) => String(w.year)))).sort((a, b) => b.localeCompare(a))],
    [],
  );

  const filtered = useMemo(() => {
    let list = works.filter(
      (w) => (type === '全部' || w.type === type) && (year === '全部' || String(w.year) === year),
    );
    list = list.sort((a, b) =>
      sort === 'latest' ? b.year - a.year : b.rating - a.rating,
    );
    return list;
  }, [type, year, sort]);

  const shown = filtered.slice(0, visible);

  const reset = () => {
    setType('全部');
    setYear('全部');
    setSort('latest');
    setVisible(STEP);
  };

  return (
    <>
      <Seo
        title="深田咏美影视作品-电影电视剧纪录片全集｜深田咏美作品"
        description="深田咏美影视作品全集，收录她的电影、电视剧与纪录片作品，支持按类型、年份与热度筛选，查看每部作品的详细介绍。"
        keywords="深田咏美作品,深田咏美影视,深田咏美电影,深田咏美电视剧,深田咏美纪录片"
      />

      <section className="border-b border-background-200 bg-foreground-950">
        <div className="max-w-6xl mx-auto px-4 py-12 md:px-8 md:py-16">
          <div className="mb-5 [&_*]:text-background-300">
            <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '影视作品' }]} />
          </div>
          <h1 className="font-heading text-3xl font-black text-background-50 md:text-4xl">影视作品</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-300">
            浏览深田咏美的电影、电视剧与纪录片作品，按类型、年份与热度筛选，快速找到你想了解的影片。
          </p>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="max-w-6xl mx-auto px-4 py-10 md:px-8 md:py-14">
          <div className="rounded-lg border border-background-200 bg-background-50">
            <button
              type="button"
              onClick={() => setFilterOpen((v) => !v)}
              className="flex w-full items-center justify-between px-5 py-4 md:hidden cursor-pointer"
            >
              <span className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-900">
                <i className="ri-filter-3-line text-primary-600"></i>
                筛选与排序
              </span>
              <i className={filterOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>
            </button>

            <div className={`${filterOpen ? 'block' : 'hidden'} px-5 pb-5 md:block md:p-5`}>
              <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-8">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-secondary-500">类型</span>
                    {types.map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => {
                          setType(t);
                          setVisible(STEP);
                        }}
                        className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors cursor-pointer ${
                          type === t
                            ? 'bg-primary-500 text-background-50'
                            : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-secondary-500">年份</span>
                    {years.map((y) => (
                      <button
                        key={y}
                        type="button"
                        onClick={() => {
                          setYear(y);
                          setVisible(STEP);
                        }}
                        className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors cursor-pointer ${
                          year === y
                            ? 'bg-accent-500 text-background-50'
                            : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                        }`}
                      >
                        {y}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-secondary-500">排序</span>
                  <div className="flex rounded-full bg-background-100 p-1">
                    {[
                      { key: 'latest', label: '最新' },
                      { key: 'popular', label: '热门' },
                    ].map((s) => (
                      <button
                        key={s.key}
                        type="button"
                        onClick={() => {
                          setSort(s.key as 'latest' | 'popular');
                          setVisible(STEP);
                        }}
                        className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors cursor-pointer ${
                          sort === s.key ? 'bg-background-50 text-primary-700' : 'text-foreground-600'
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <p className="mt-6 text-sm text-foreground-500">
            共找到 <strong className="text-primary-600">{filtered.length}</strong> 部作品
          </p>

          {shown.length > 0 ? (
            <>
              <div className="mt-6 grid grid-cols-2 gap-4 md:gap-6 lg:grid-cols-3">
                {shown.map((item) => (
                  <WorkCard key={item.id} item={item} />
                ))}
              </div>

              {visible < filtered.length && (
                <div className="mt-10 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setVisible((v) => v + STEP)}
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-6 py-3 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 cursor-pointer"
                  >
                    <i className="ri-add-line"></i>
                    加载更多作品
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100/60 py-16 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-film-line text-2xl"></i>
              </span>
              <p className="mt-4 font-heading text-lg font-bold text-foreground-900">没有符合条件的作品</p>
              <p className="mt-1 text-sm text-foreground-500">尝试更换类型或年份，或重置全部筛选条件。</p>
              <button
                type="button"
                onClick={reset}
                className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-refresh-line"></i>
                重置筛选
              </button>
            </div>
          )}
        </div>
      </section>
    </>
  );
}